// Toppers UtilityPage left side-nav, rendered in-tree.
//
// The `page.nav` (NavBar snippet) `body` is rendered TWICE -- a desktop `<nav>`
// and a mobile `<nav>` (with a MENU accordion toggle) -- iterating the same
// items. It renders ONLY the `link` and `link_list` block types and SILENTLY
// SKIPS any other type (e.g. the snippet's `html` block); the chrome nav builder
// filters to those two.
//
// A PageChooserBlock value serializes to a page id client-side, NOT a resolved
// `url`, so the href + active state are RESOLVED SERVER-SIDE into the `NavItem`
// props. React just renders the pre-resolved `href` + `is_active`.
//
// Each `link_list` group and the mobile MENU toggle own their open/close state:
// clicking toggles `accordion--is-active` on the trigger and
// `accordion-target--is-active` on its target, tracking `aria-expanded`.
import { Immutable } from "immer";
import React, { useState } from "react";
import { t } from "ttag";

import { templates } from "@reactivated";

type INavItem = Immutable<templates.UtilityPageTemplate["nav_items"][number]>;

const NavLinkItem = ({ item }: { item: INavItem }): React.JSX.Element => (
    <div className="accordion__list-item">
        <a
            href={item.href}
            className={item.is_active ? "accordion--is-active" : undefined}
        >
            {item.display_name}
        </a>
    </div>
);

// `active` seeds the open state for the FIRST desktop `link_list`, reproducing
// the legacy UtilityPage which rendered that group expanded on load (the
// identical mobile copy stays closed). classList order was base-then-modifier
// ("accordion accordion--is-active"); render it in that order so the SSR markup
// matches the hydrated DOM.
const NavListItem = ({
    item,
    active,
}: {
    item: INavItem;
    active: boolean;
}): React.JSX.Element => {
    const [isOpen, setIsOpen] = useState(active);
    return (
        <div className="accordion__list-item">
            <button
                className={
                    isOpen ? "accordion accordion--is-active" : "accordion"
                }
                aria-expanded={isOpen ? "true" : "false"}
                aria-controls="menu-content"
                onClick={() => {
                    setIsOpen((open) => !open);
                }}
            >
                {item.list_header} <span />
            </button>
            <div
                id="menu-content"
                className={
                    isOpen
                        ? "accordion-target accordion-target--is-active"
                        : "accordion-target"
                }
            >
                <ul className="accordion-target__list">
                    {item.links.map((link, i) => (
                        <li key={i} className="accordion-target__list-item">
                            <a
                                href={link.href}
                                className={
                                    link.is_active ? "is-active" : undefined
                                }
                            >
                                {link.display_name}
                            </a>
                        </li>
                    ))}
                </ul>
            </div>
        </div>
    );
};

const NavItems = ({
    items,
    variant,
}: {
    items: readonly INavItem[];
    variant: "desktop" | "mobile";
}): React.JSX.Element => {
    // The first link_list (NavListItem) in the DESKTOP nav renders expanded on
    // load; the same item in the mobile nav stays closed.
    const firstListIndex = items.findIndex((item) => item.kind !== "link");
    return (
        <>
            {items.map((item, i) =>
                item.kind === "link" ? (
                    <NavLinkItem key={i} item={item} />
                ) : (
                    <NavListItem
                        key={i}
                        item={item}
                        active={variant === "desktop" && i === firstListIndex}
                    />
                ),
            )}
        </>
    );
};

// Mobile MENU toggle: reveals the mobile nav's `.accordion-target`. The trigger
// is an `<a>` with no `href` (legacy relied on a global click handler), so it is
// exposed as a keyboard-operable `role="button"` with `aria-expanded`; it starts
// closed.
const UtilitySideNavMobile = ({
    items,
}: {
    items: readonly INavItem[];
}): React.JSX.Element => {
    const [isOpen, setIsOpen] = useState(false);
    const toggle = () => {
        setIsOpen((open) => !open);
    };
    return (
        <nav
            className="accordion__list-item accordion__list-item--mobile"
            aria-label="main"
        >
            <a
                className={
                    isOpen
                        ? "category-list-side-nav__mobile-toggle category-list-side-nav__mobile-toggle--header accordion accordion--is-active"
                        : "category-list-side-nav__mobile-toggle category-list-side-nav__mobile-toggle--header accordion"
                }
                role="button"
                tabIndex={0}
                aria-expanded={isOpen ? "true" : "false"}
                onClick={toggle}
                onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        toggle();
                    }
                }}
            >
                {t`MENU`}
            </a>
            <div
                className={
                    isOpen
                        ? "accordion-target accordion-target--is-active"
                        : "accordion-target"
                }
            >
                <NavItems items={items} variant="mobile" />
            </div>
        </nav>
    );
};

export const UtilitySideNav = ({
    items,
}: {
    items: readonly INavItem[];
}): React.JSX.Element => (
    <>
        <nav
            className="accordion__list-item accordion__list-item--desktop"
            aria-label="main"
        >
            <NavItems items={items} variant="desktop" />
        </nav>

        <UtilitySideNavMobile items={items} />
    </>
);
